import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { DEFAULT_SITE_CONDITIONS } from '../../domain/foundation/siteConditions';
import { normalizeClimateProfile } from '../../domain/location/types';
import { normalizeRoofConfig } from '../../domain/roof/roofConfig';
import { getEngine } from '../../engines/engineRegistry';
import type { HeaterType, RoofKind, TwoRoomConfig } from '../../engines/types';
import { useMarket } from '../../i18n';
import { decodeShareConfig, type ShareableConfig } from '../../lib/shareConfig';
import { isSaunaType, type SaunaType } from '../../types/design';
import { getNextWorkspaceMeta, getWorkspaceMeta, parseWorkspace } from './workspaces';

function isTwoRoomEntryWall(value: string | null): value is TwoRoomConfig['entryWall'] {
  return value === 'short' || value === 'long';
}

function isTwoRoomGlazingStyle(value: string | null): value is TwoRoomConfig['glazingStyle'] {
  return value === 'standard' || value === 'panorama';
}

function isTwoRoomSaunaPosition(value: string | null): value is TwoRoomConfig['saunaPosition'] {
  return value === 'left' || value === 'right';
}

function parseBooleanParam(value: string | null): boolean | undefined {
  if (value === 'true' || value === '1' || value === 'yes') return true;
  if (value === 'false' || value === '0' || value === 'no') return false;
  return undefined;
}

const DEFAULT_SAUNA_TYPE: SaunaType = 'fyrkantig';

export function useBuilderRouting() {
  const { type: typeParam = DEFAULT_SAUNA_TYPE } = useParams<{ type: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { search } = useLocation();

  // The :type segment is user-controlled; an unknown value must degrade to the
  // default type (and redirect to the canonical URL) instead of reaching the
  // Record<SaunaType, …> lookups downstream with a key they don't have.
  const type: SaunaType = isSaunaType(typeParam) ? typeParam : DEFAULT_SAUNA_TYPE;

  // Replace-navigate to another type's canonical URL, keeping search params.
  // Also used by the design lifecycle when a loaded design's type disagrees
  // with the URL, so autosave can never write the route's type over the row's.
  const navigateToType = useCallback(
    (nextType: SaunaType) => {
      navigate({ pathname: `/builder/${nextType}`, search }, { replace: true });
    },
    [navigate, search],
  );

  useEffect(() => {
    if (typeParam !== type) {
      navigateToType(type);
    }
  }, [navigateToType, type, typeParam]);
  const engine = useMemo(() => getEngine(type), [type]);
  // The one market the app resolved (baked <html data-market> first). Deriving
  // it again here from the URL/hostname let the builder's tabs, prices and rules
  // run in one market while useT() rendered the chrome in the other.
  const market = useMarket();
  const designId = searchParams.get('id');
  const activeWorkspace = parseWorkspace(searchParams.get('view'));
  const activeWorkspaceMeta = getWorkspaceMeta(activeWorkspace, market);
  const nextWorkspaceMeta = getNextWorkspaceMeta(activeWorkspaceMeta, market);
  const widthParam = searchParams.get('w');
  const lengthParam = searchParams.get('l');
  const wizardZip = searchParams.get('zip') ?? '';
  const wizardClimate = searchParams.get('climate') ?? '';
  const wizardHeat = searchParams.get('heat');
  const wizardPeopleRaw = searchParams.get('people');
  const roofKindRaw = searchParams.get('roof');
  const pitchRaw = searchParams.get('pitch');
  const roofSystemRaw = searchParams.get('roofSystem');
  const runoffRaw = searchParams.get('runoff');
  const plinthRaw = searchParams.get('plinth');
  const buriedRaw = searchParams.get('bury');
  const terrainMode = searchParams.get('terrain');
  const slopeAcrossRaw = searchParams.get('slopeX');
  const slopeAlongRaw = searchParams.get('slopeZ');
  const shareParam = searchParams.get('share');
  const entryWallRaw = searchParams.get('entryWall');
  const glazingStyleRaw = searchParams.get('glazingStyle') ?? searchParams.get('glazing');
  const saunaPositionRaw = searchParams.get('saunaPosition');
  const hasRelaxRoomRaw = searchParams.get('hasRelaxRoom') ?? searchParams.get('relax');
  const antechamberRaw = searchParams.get('ante') ?? searchParams.get('antechamber');

  // Decode an encoded share config passed via ?share= for lossless roundtrips
  const shareConfig: ShareableConfig | null = useMemo(
    () => (shareParam ? decodeShareConfig(shareParam) : null),
    [shareParam],
  );

  useEffect(() => {
    const currentWorkspace = searchParams.get('view');
    if (currentWorkspace !== activeWorkspace) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('view', activeWorkspace);
      setSearchParams(nextParams, { replace: true });
    }
  }, [activeWorkspace, searchParams, setSearchParams]);

  const mergeSearchParams = useCallback(
    (nextInit: URLSearchParams | Record<string, string>, opts?: { replace?: boolean }) => {
      const nextParams = new URLSearchParams(searchParams);

      if (nextInit instanceof URLSearchParams) {
        nextInit.forEach((value, key) => {
          if (value === '') {
            nextParams.delete(key);
          } else {
            nextParams.set(key, value);
          }
        });
      } else {
        Object.entries(nextInit).forEach(([key, value]) => {
          if (value === '') {
            nextParams.delete(key);
          } else {
            nextParams.set(key, value);
          }
        });
      }

      setSearchParams(nextParams, opts);
    },
    [searchParams, setSearchParams],
  );

  const setActiveWorkspace = useCallback(
    (workspace: typeof activeWorkspace) => {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('view', workspace);
      setSearchParams(nextParams);
    },
    [searchParams, setSearchParams],
  );

  const initialDimensions = useMemo(() => {
    if (widthParam && lengthParam) {
      const width = Number.parseFloat(widthParam);
      const length = Number.parseFloat(lengthParam);

      if (!Number.isNaN(width) && !Number.isNaN(length) && width > 0 && length > 0) {
        return { width, length };
      }
    }

    return engine.defaultDimensions;
  }, [engine.defaultDimensions, lengthParam, widthParam]);

  const initialThermalInputs = useMemo(
    () => ({
      heaterType: (wizardHeat === 'electric' ? 'electric' : 'wood') as HeaterType,
    }),
    [wizardHeat],
  );

  // The wizard's size-tier choice ("2"/"4"/"6" personer) never becomes engine
  // geometry — it only ever picked a width/length preset. Seeds the store's
  // persisted `wizardCapacityHint` for a FRESH draft only; a reopened design
  // or share link gets its hint from the saved row / share payload instead
  // (see useBuilderDesignLifecycle), never from this URL param.
  const initialWizardCapacityHint = useMemo(() => {
    const parsed = wizardPeopleRaw ? Number.parseInt(wizardPeopleRaw, 10) : Number.NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [wizardPeopleRaw]);

  const initialPartitionSplit = useMemo(() => {
    const antechamberLength = antechamberRaw ? Number.parseFloat(antechamberRaw) : Number.NaN;
    if (
      type === 'tvarum'
      && Number.isFinite(antechamberLength)
      && antechamberLength > 0
      && antechamberLength < initialDimensions.length
    ) {
      return 1 - antechamberLength / initialDimensions.length;
    }

    return 0.5;
  }, [antechamberRaw, initialDimensions.length, type]);

  const twoRoomConfig = useMemo<Partial<TwoRoomConfig>>(() => {
    const hasRelaxRoom = parseBooleanParam(hasRelaxRoomRaw);
    return {
      ...(shareConfig?.entryWall ? { entryWall: shareConfig.entryWall } : {}),
      ...(shareConfig?.glazingStyle ? { glazingStyle: shareConfig.glazingStyle } : {}),
      ...(shareConfig?.saunaPosition ? { saunaPosition: shareConfig.saunaPosition } : {}),
      ...(shareConfig?.hasRelaxRoom !== undefined ? { hasRelaxRoom: shareConfig.hasRelaxRoom } : {}),
      ...(isTwoRoomEntryWall(entryWallRaw) ? { entryWall: entryWallRaw } : {}),
      ...(isTwoRoomGlazingStyle(glazingStyleRaw) ? { glazingStyle: glazingStyleRaw } : {}),
      ...(isTwoRoomSaunaPosition(saunaPositionRaw) ? { saunaPosition: saunaPositionRaw } : {}),
      ...(hasRelaxRoom !== undefined ? { hasRelaxRoom } : {}),
    };
  }, [entryWallRaw, glazingStyleRaw, hasRelaxRoomRaw, saunaPositionRaw, shareConfig]);

  const initialRoofConfig = useMemo(() => {
    const pitchDeg = pitchRaw ? Number(pitchRaw) : Number.NaN;
    const roofKind = roofKindRaw === 'mono' || roofKindRaw === 'gable' || roofKindRaw === 'barrel'
      ? roofKindRaw as RoofKind
      : undefined;

    return normalizeRoofConfig(type, {
      ...(roofKind ? { kind: roofKind } : {}),
      ...(Number.isFinite(pitchDeg) ? { pitchDeg } : {}),
      ...(roofSystemRaw === 'felt_roof' || roofSystemRaw === 'sheet_metal' ? { systemId: roofSystemRaw } : {}),
      ...(runoffRaw === 'free_drip' || runoffRaw === 'gutter_kit' ? { runoff: runoffRaw } : {}),
    } as Partial<import('../../engines/types').PitchedRoofConfig>);
  }, [pitchRaw, roofKindRaw, roofSystemRaw, runoffRaw, type]);

  const initialSiteConditions = useMemo(() => {
    const plinthLengthMm = plinthRaw ? Number(plinthRaw) : Number.NaN;
    const minBuriedDepthMm = buriedRaw ? Number(buriedRaw) : Number.NaN;
    const slopeAcrossPercent = slopeAcrossRaw ? Number(slopeAcrossRaw) : Number.NaN;
    const slopeAlongPercent = slopeAlongRaw ? Number(slopeAlongRaw) : Number.NaN;

    return {
      ...(Number.isFinite(plinthLengthMm) && [500, 700, 900].includes(plinthLengthMm)
        ? { plinthLengthMm: plinthLengthMm as 500 | 700 | 900 }
        : {}),
      ...(Number.isFinite(minBuriedDepthMm) ? { minBuriedDepthMm } : {}),
      ...(terrainMode === 'flat' || terrainMode === 'slope' ? { terrainMode } : {}),
      ...(Number.isFinite(slopeAcrossPercent) ? { slopeAcrossPercent } : {}),
      ...(Number.isFinite(slopeAlongPercent) ? { slopeAlongPercent } : {}),
    } satisfies Partial<typeof DEFAULT_SITE_CONDITIONS>;
  }, [buriedRaw, plinthRaw, slopeAcrossRaw, slopeAlongRaw, terrainMode]);

  const initialClimateProfile = useMemo(() => {
    if (!wizardClimate) {
      return normalizeClimateProfile(undefined);
    }

    return normalizeClimateProfile({ snowLoadClass: wizardClimate as import('../../domain/location/types').ClimateProfile['snowLoadClass'] });
  }, [wizardClimate]);

  const initialSeedKey = useMemo(
    () => JSON.stringify({
      market,
      type,
      w: widthParam ?? '',
      l: lengthParam ?? '',
      roof: roofKindRaw ?? '',
      pitch: pitchRaw ?? '',
      roofSystem: roofSystemRaw ?? '',
      runoff: runoffRaw ?? '',
      heat: wizardHeat ?? '',
      ante: antechamberRaw ?? '',
      entryWall: entryWallRaw ?? '',
      glazingStyle: glazingStyleRaw ?? '',
      saunaPosition: saunaPositionRaw ?? '',
      hasRelaxRoom: hasRelaxRoomRaw ?? '',
      zip: wizardZip,
      plinth: plinthRaw ?? '',
      bury: buriedRaw ?? '',
      terrain: terrainMode ?? '',
      slopeX: slopeAcrossRaw ?? '',
      slopeZ: slopeAlongRaw ?? '',
      climate: wizardClimate,
    }),
    [antechamberRaw, buriedRaw, entryWallRaw, glazingStyleRaw, hasRelaxRoomRaw, lengthParam, market, pitchRaw, plinthRaw, roofKindRaw, roofSystemRaw, runoffRaw, saunaPositionRaw, slopeAcrossRaw, slopeAlongRaw, terrainMode, type, widthParam, wizardClimate, wizardHeat, wizardZip],
  );

  return {
    market,
    type,
    engine,
    designId,
    activeWorkspace,
    activeWorkspaceMeta,
    nextWorkspaceMeta,
    mergeSearchParams,
    navigateToType,
    setActiveWorkspace,
    hasSeedDimensions: Boolean(
      widthParam && lengthParam &&
      Number(widthParam) > 0 && Number(lengthParam) > 0 &&
      Number.isFinite(Number(widthParam)) && Number.isFinite(Number(lengthParam)),
    ),
    initialDimensions,
    initialThermalInputs,
    initialPartitionSplit,
    twoRoomConfig,
    initialRoofConfig,
    initialSiteConditions,
    initialClimateProfile,
    initialSeedKey,
    wizardZip,
    wizardClimate,
    initialWizardCapacityHint,
    shareConfig,
  };
}
